import * as React from 'react';
import { type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { distinctValues, FormCreatableCombobox } from './FormCreatableCombobox';

export interface FormLocationSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  placeholder?: string;
}

/**
 * React Hook Form adapter for a person's Standort: suggests the distinct
 * Standorte people already have, while still allowing a new one via "Create".
 */
export function FormLocationSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormLocationSelectProps<TFieldValues, TName>) {
  const { data: members, isLoading } = useOrgMembers();

  const locations = React.useMemo(
    () => distinctValues(members?.map((member) => member.location)),
    [members]
  );

  return <FormCreatableCombobox {...props} options={locations} isLoading={isLoading} />;
}

FormLocationSelect.displayName = 'FormLocationSelect';
